import React, { useState } from 'react';
import { FontItem } from '../types';

interface FontModalProps {
  fonts: FontItem[];
  currentFont: FontItem;
  onSelectFont: (font: FontItem) => void;
  onClose: () => void;
}

const FontModal: React.FC<FontModalProps> = ({
  fonts,
  currentFont,
  onSelectFont,
  onClose,
}) => {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleSelect = async (font: FontItem) => {
    if (font.isBuiltIn && font.url) {
      // Check if the font is already available in the document
      const alreadyLoaded = document.fonts.check(`16px "${font.family}"`);
      if (!alreadyLoaded) {
        setLoadingId(font.id);
        try {
          onSelectFont(font); // triggers the async load in App
        } finally {
          setLoadingId(null);
        }
        return;
      }
    }
    onSelectFont(font);
  };

  const isLoading = (fontId: string) => loadingId === fontId;

  const builtInFonts = fonts.filter((f) => f.isBuiltIn);
  const customFonts = fonts.filter((f) => f.isCustom);
  const defaultFonts = fonts.filter((f) => !f.isBuiltIn && !f.isCustom);

  const renderFontItem = (font: FontItem) => {
    const loading = isLoading(font.id);
    return (
      <div
        key={font.id}
        onClick={() => !loading && handleSelect(font)}
        className={`flex items-center p-3 rounded-lg ${
          loading
            ? 'bg-gray-50 cursor-wait'
            : currentFont.id === font.id
              ? 'bg-[#3B82F6]/10 border border-[#3B82F6]'
              : 'hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium flex items-center gap-2">
            {font.name}
            {font.isBuiltIn && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded shrink-0">
                在线安装
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">{font.desc}</div>
        </div>
        {loading ? (
          <i className="fa fa-spinner fa-spin text-gray-400 ml-2"></i>
        ) : currentFont.id === font.id ? (
          <i className="fa fa-check text-[#3B82F6] ml-2"></i>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white w-full rounded-t-2xl p-4 max-h-[70%] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">选择字体</h3>
          <button onClick={onClose} className="text-gray-400">
            <i className="fa fa-times text-xl"></i>
          </button>
        </div>

        <div className="space-y-4">
          {defaultFonts.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 mb-2">系统字体</div>
              <div className="space-y-2">{defaultFonts.map(renderFontItem)}</div>
            </div>
          )}

          {builtInFonts.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 mb-2">内置字体（首次使用需下载）</div>
              <div className="space-y-2">{builtInFonts.map(renderFontItem)}</div>
            </div>
          )}

          {customFonts.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 mb-2">自定义字体</div>
              <div className="space-y-2">{customFonts.map(renderFontItem)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FontModal;
